<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

export default {
  name: 'StandaloneTicketModal',
  components: {
    Avatar,
    Icon,
  },
  props: {
    onClose: {
      type: Function,
      required: true,
    },
    accountId: {
      type: [String, Number],
      required: true,
    },
  },
  emits: ['created'],
  data() {
    return {
      title: '',
      description: '',
      status: 'open',
      urgency: 'Baixa',
      service: '',
      category: '',
      dueDate: '',
      assigneeId: null,
      teamId: null,
      contactId: null,
      isSubmitting: false,
      contactQuery: '',
      contactSearchResults: [],
      isSearchingContact: false,
      searchTimeout: null,
      selectedContact: null,
    };
  },
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
      teams: 'teams/getTeams',
      currentUser: 'getCurrentUser',
      getAccount: 'accounts/getAccount',
    }),
    currentAccount() {
      return this.getAccount(this.accountId);
    },
    ticketServices() {
      return this.currentAccount?.custom_attributes?.ticket_services || [];
    },
    ticketCategories() {
      return this.currentAccount?.custom_attributes?.ticket_categories || [];
    },
    ticketUrgencies() {
      const urgencies =
        this.currentAccount?.custom_attributes?.ticket_urgencies;
      if (Array.isArray(urgencies) && urgencies.length > 0) return urgencies;
      return [
        {
          name: 'Baixa',
          label: this.$t('TICKET_DETAILS.URGENCY_LOW'),
          color: '#3b82f6',
        },
        {
          name: 'Média',
          label: this.$t('TICKET_DETAILS.URGENCY_MEDIUM'),
          color: '#eab308',
        },
        {
          name: 'Alta',
          label: this.$t('TICKET_DETAILS.URGENCY_HIGH'),
          color: '#f97316',
        },
        {
          name: 'Urgente',
          label: this.$t('TICKET_DETAILS.URGENCY_URGENT'),
          color: '#ef4444',
        },
      ];
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('teams/get');
    // Preenche com o prório usuário se não for admin
    if (this.currentUser.role !== 'administrator') {
      this.assigneeId = this.currentUser.id;
    }
  },
  methods: {
    assignToMe() {
      this.assigneeId = this.currentUser.id;
    },
    assignToMyTeam() {
      const team = this.teams.find(item =>
        item.members?.some(member => member.id === this.currentUser.id)
      );
      if (!team) {
        useAlert(this.$t('TICKETS.ALERTS.NO_AVAILABLE_TEAM'));
        return;
      }
      this.teamId = team.id;
    },
    async submitTicket() {
      if (!this.title.trim()) {
        useAlert(this.$t('TICKETS.ALERTS.TITLE_REQUIRED'));
        return;
      }
      this.isSubmitting = true;
      try {
        await this.$store.dispatch('tickets/create', {
          title: this.title,
          description: this.description,
          status: this.status,
          urgency: this.urgency,
          assignee_id: this.assigneeId,
          team_id: this.teamId,
          contact_id: this.contactId,
          service: this.service,
          category: this.category,
          due_date: this.dueDate,
        });
        useAlert(this.$t('TICKETS.ALERTS.CREATED'));
        this.$emit('created');
        this.onClose();
      } catch (error) {
        useAlert(this.$t('TICKETS.ALERTS.CREATE_ERROR'));
      } finally {
        this.isSubmitting = false;
      }
    },
    onContactSearchInput() {
      if (this.contactQuery.trim().length < 3) {
        this.contactSearchResults = [];
        return;
      }
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(async () => {
        this.isSearchingContact = true;
        try {
          const response = await window.axios.get(
            `/api/v1/accounts/${this.accountId}/contacts/search?q=${encodeURIComponent(this.contactQuery)}`
          );
          this.contactSearchResults = response.data.payload || [];
        } catch (e) {
          this.contactSearchResults = [];
        } finally {
          this.isSearchingContact = false;
        }
      }, 500);
    },
    selectContact(res) {
      this.selectedContact = res;
      this.contactId = res.id;
      this.contactQuery = '';
      this.contactSearchResults = [];
    },
    removeContact() {
      this.selectedContact = null;
      this.contactId = null;
      this.contactQuery = '';
    },
  },
};
</script>

<template>
  <div
    role="dialog"
    aria-modal="true"
    aria-labelledby="standalone-ticket-heading"
    class="flex flex-col h-full w-full mx-auto"
  >
    <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
      <h2
        id="standalone-ticket-heading"
        class="text-lg font-bold text-slate-800 dark:text-slate-200"
      >
        {{ $t('CREATE_TICKET.STANDALONE') }}
      </h2>
    </div>

    <div class="p-6 flex-1 overflow-y-auto">
      <form class="space-y-4" @submit.prevent="submitTicket">
        <!-- Solicitante -->
        <div class="relative">
          <label
            for="standalone-ticket-contact"
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >{{ $t('CREATE_TICKET.REQUESTER') }}</label
          >

          <!-- Contato Selecionado -->
          <div
            v-if="selectedContact"
            class="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 relative"
          >
            <Avatar
              :src="selectedContact.thumbnail"
              :name="selectedContact.name"
              :size="40"
              rounded-full
            />
            <div class="min-w-0 flex-1">
              <p
                class="text-sm font-medium text-slate-900 dark:text-white truncate"
              >
                {{ selectedContact.name }}
              </p>
              <p class="text-xs text-slate-500 truncate">
                {{ selectedContact.email }}
              </p>
            </div>
            <button
              type="button"
              class="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 transition-colors"
              :title="$t('CREATE_TICKET.REMOVE_CONTACT')"
              @click="removeContact"
            >
              <Icon icon="i-lucide-x" class="size-4" />
            </button>
          </div>

          <!-- Modo Busca -->
          <div v-else class="relative">
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <Icon icon="i-lucide-search" class="size-4 text-slate-400" />
              </div>
              <input
                id="standalone-ticket-contact"
                v-model="contactQuery"
                type="text"
                class="w-full !pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500 dark:text-white"
                :placeholder="$t('CREATE_TICKET.SEARCH_CONTACT')"
                @input="onContactSearchInput"
              />
            </div>

            <!-- Dropdown de Resultados -->
            <div
              v-if="contactQuery.length >= 3"
              class="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            >
              <div
                v-if="isSearchingContact"
                class="p-3 text-sm text-slate-500 text-center"
              >
                {{ $t('CREATE_TICKET.SEARCHING') }}
              </div>
              <div
                v-else-if="contactSearchResults.length === 0"
                class="p-3 text-sm text-slate-500 text-center"
              >
                {{ $t('CREATE_TICKET.NO_CONTACTS') }}
              </div>
              <ul v-else class="py-1">
                <li
                  v-for="res in contactSearchResults"
                  :key="res.id"
                  role="button"
                  tabindex="0"
                  :aria-label="
                    $t('CREATE_TICKET.SELECT_CONTACT', { name: res.name })
                  "
                  class="px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-3"
                  @click="selectContact(res)"
                  @keydown.enter="selectContact(res)"
                  @keydown.space.prevent="selectContact(res)"
                >
                  <Avatar
                    :src="res.thumbnail"
                    :name="res.name"
                    :size="32"
                    rounded-full
                  />
                  <div class="min-w-0">
                    <p
                      class="text-sm font-medium text-slate-900 dark:text-white truncate"
                    >
                      {{ res.name }}
                    </p>
                    <p class="text-xs text-slate-500 truncate">
                      {{ res.email }}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="space-y-5">
          <div>
            <label
              for="standalone-ticket-title"
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >{{ $t('CREATE_TICKET.SUBJECT') }}</label
            >
            <input
              id="standalone-ticket-title"
              v-model="title"
              type="text"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:text-slate-100"
              :placeholder="$t('CREATE_TICKET.SUBJECT_PLACEHOLDER')"
            />
          </div>

          <div>
            <label
              for="standalone-ticket-description"
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >{{ $t('CREATE_TICKET.DESCRIPTION') }}</label
            >
            <textarea
              id="standalone-ticket-description"
              v-model="description"
              rows="3"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:text-slate-100"
              :placeholder="$t('CREATE_TICKET.DESCRIPTION_TICKET_PLACEHOLDER')"
            />
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                for="standalone-ticket-service"
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                >{{ $t('CREATE_TICKET.SERVICE') }}</label
              >
              <div class="relative w-full">
                <select
                  id="standalone-ticket-service"
                  v-model="service"
                  class="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:text-slate-100 appearance-none !bg-none"
                >
                  <option value="">{{ $t('CREATE_TICKET.SELECT') }}</option>
                  <option
                    v-for="svc in ticketServices"
                    :key="svc.name"
                    :value="svc.name"
                  >
                    {{ svc.name }}
                  </option>
                </select>
                <div
                  class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500"
                >
                  <Icon icon="i-lucide-chevron-down" class="w-4 h-4" />
                </div>
              </div>
            </div>
            <div>
              <label
                for="standalone-ticket-category"
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                >{{ $t('CREATE_TICKET.CATEGORY') }}</label
              >
              <div class="relative w-full">
                <select
                  id="standalone-ticket-category"
                  v-model="category"
                  class="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:text-slate-100 appearance-none !bg-none"
                >
                  <option value="">{{ $t('CREATE_TICKET.SELECT') }}</option>
                  <option
                    v-for="cat in ticketCategories"
                    :key="cat.name"
                    :value="cat.name"
                  >
                    {{ cat.name }}
                  </option>
                </select>
                <div
                  class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500"
                >
                  <Icon icon="i-lucide-chevron-down" class="w-4 h-4" />
                </div>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                for="standalone-ticket-status"
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                >{{ $t('CREATE_TICKET.STATUS') }}</label
              >
              <div class="relative w-full">
                <select
                  id="standalone-ticket-status"
                  v-model="status"
                  class="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:text-slate-100 appearance-none !bg-none"
                >
                  <option value="open">{{ $t('CREATE_TICKET.OPEN') }}</option>
                  <option value="in_progress">
                    {{ $t('CREATE_TICKET.IN_PROGRESS') }}
                  </option>
                  <option value="pending">
                    {{ $t('CREATE_TICKET.PENDING') }}
                  </option>
                  <option value="resolved">
                    {{ $t('CREATE_TICKET.RESOLVED') }}
                  </option>
                  <option value="closed">
                    {{ $t('CREATE_TICKET.CLOSED') }}
                  </option>
                </select>
                <div
                  class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div>
              <label
                for="standalone-ticket-urgency"
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                >{{ $t('CREATE_TICKET.URGENCY') }}</label
              >
              <div class="relative w-full">
                <select
                  id="standalone-ticket-urgency"
                  v-model="urgency"
                  class="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:text-slate-100 appearance-none !bg-none"
                >
                  <option
                    v-for="urg in ticketUrgencies"
                    :key="urg.name"
                    :value="urg.name"
                  >
                    {{ urg.label || urg.name }}
                  </option>
                </select>
                <div
                  class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500"
                >
                  <Icon icon="i-lucide-chevron-down" class="w-4 h-4" />
                </div>
              </div>
            </div>
          </div>

          <div>
            <label
              for="standalone-ticket-due-date"
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >{{ $t('CREATE_TICKET.DUE_DATE') }}</label
            >
            <input
              id="standalone-ticket-due-date"
              v-model="dueDate"
              type="datetime-local"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:text-slate-100"
            />
          </div>
        </div>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <div class="flex items-center justify-between mb-1">
              <label
                for="standalone-ticket-assignee"
                class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                >{{ $t('CREATE_TICKET.ASSIGNEE') }}</label
              >
              <button
                type="button"
                class="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none"
                :title="$t('CREATE_TICKET.ASSIGN_TO_ME')"
                @click="assignToMe"
              >
                <span class="i-lucide-user h-4 w-4" />
              </button>
            </div>

            <div class="relative w-full">
              <select
                id="standalone-ticket-assignee"
                v-model="assigneeId"
                class="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:text-slate-100 appearance-none !bg-none"
              >
                <option :value="null">{{ $t('CREATE_TICKET.NONE') }}</option>
                <option
                  v-for="agent in agents"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
              <div
                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div>
            <div class="flex items-center justify-between mb-1">
              <label
                for="standalone-ticket-team"
                class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                >{{ $t('CREATE_TICKET.TEAM') }}</label
              >
              <button
                type="button"
                class="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none"
                :title="$t('CREATE_TICKET.ASSIGN_TO_TEAM')"
                @click="assignToMyTeam"
              >
                <span class="i-lucide-users h-4 w-4" />
              </button>
            </div>
            <div class="relative w-full">
              <select
                id="standalone-ticket-team"
                v-model="teamId"
                class="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:text-slate-100 appearance-none !bg-none"
              >
                <option :value="null">{{ $t('CREATE_TICKET.NONE') }}</option>
                <option v-for="team in teams" :key="team.id" :value="team.id">
                  {{ team.name }}
                </option>
              </select>
              <div
                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>

    <div class="px-6 py-4 flex justify-end space-x-3 mt-4">
      <button
        class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm"
        @click="onClose"
      >
        {{ $t('CREATE_TICKET.CANCEL') }}
      </button>
      <button
        :disabled="isSubmitting"
        class="px-4 py-2 text-sm font-medium text-white bg-woot-500 border border-transparent rounded-md hover:bg-woot-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-woot-500 shadow-sm disabled:opacity-50"
        @click="submitTicket"
      >
        {{
          isSubmitting
            ? $t('CREATE_TICKET.CREATING')
            : $t('CREATE_TICKET.CREATE')
        }}
      </button>
    </div>
  </div>
</template>
